import { useEffect, useRef, useState } from 'react';
import { Copy, Mail, Phone, Trash2, Check } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { StatusPill } from './StatusPill';
import { answerText, formatDateTime } from './adminApi';

export const SubmissionDrawer = ({ entry, sections, onClose, onStatusChange, onSaveNotes, onDelete }) => {
  const [notes, setNotes] = useState('');
  const [noteState, setNoteState] = useState('idle');
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const loadedId = useRef(null);

  useEffect(() => {
    if (entry && loadedId.current !== entry.id) { loadedId.current = entry.id; setNotes(entry.notes || ''); setNoteState('idle'); setConfirming(false); }
    if (!entry) loadedId.current = null;
  }, [entry]);

  useEffect(() => {
    if (!entry || noteState !== 'dirty') return;
    const timer = setTimeout(async () => {
      setNoteState('saving');
      const ok = await onSaveNotes(entry, notes);
      setNoteState(ok ? 'saved' : 'dirty');
    }, 900);
    return () => clearTimeout(timer);
  }, [notes, noteState, entry, onSaveNotes]);

  if (!entry) return null;
  const email = answerText(entry.answers.email);
  const phone = answerText(entry.answers.phone);
  const company = answerText(entry.answers.companyName) || 'Submission';

  const copyEmail = async () => { await navigator.clipboard?.writeText(email); setCopied(true); setTimeout(() => setCopied(false), 1800); };

  return <Sheet open onOpenChange={open => { if (!open) onClose(); }}>
    <SheetContent side="right" className="admin-drawer" data-lenis-prevent data-testid="admin-drawer">
      <div className="admin-drawer-head">
        <SheetTitle asChild><h2 data-testid="admin-drawer-company">{company}</h2></SheetTitle>
        <p data-testid="admin-drawer-meta">{answerText(entry.answers.contactName)} · Received {formatDateTime(entry.created_at)}</p>
        <div className="admin-drawer-actions">
          <StatusPill status={entry.status} id={`drawer-${entry.id}`} onChange={value => onStatusChange(entry, value)} />
          {email && <button type="button" className="admin-btn admin-btn-ghost" onClick={copyEmail} data-testid="admin-drawer-copy-email">{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copied' : 'Copy email'}</button>}
          {email && <a className="admin-btn admin-btn-ghost" href={`mailto:${email}`} data-testid="admin-drawer-mailto"><Mail size={14} />Email</a>}
          {phone && <a className="admin-btn admin-btn-ghost" href={`tel:${phone.replace(/[^+\d]/g, '')}`} data-testid="admin-drawer-tel"><Phone size={14} />Call</a>}
          {confirming
            ? <span style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="admin-btn admin-btn-danger" onClick={() => onDelete(entry)} data-testid="admin-drawer-delete-confirm"><Trash2 size={14} />Confirm delete</button>
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setConfirming(false)} data-testid="admin-drawer-delete-cancel">Cancel</button>
            </span>
            : <button type="button" className="admin-btn admin-btn-danger" onClick={() => setConfirming(true)} data-testid="admin-drawer-delete"><Trash2 size={14} />Delete</button>}
        </div>
      </div>

      <div className="admin-drawer-body">
        <div className="admin-note-block">
          <label htmlFor="admin-internal-notes">Internal notes
            <em data-testid="admin-notes-state">{noteState === 'saving' ? 'Saving…' : noteState === 'saved' ? 'Saved' : noteState === 'dirty' ? 'Unsaved' : 'Auto-saves'}</em>
          </label>
          <textarea id="admin-internal-notes" value={notes} placeholder="Call summary, next steps, owner…"
            onChange={e => { setNotes(e.target.value); setNoteState('dirty'); }} data-testid="admin-notes-input" />
        </div>
        {sections.map(section => {
          const rows = section.fields.filter(f => entry.answers[f.key]);
          if (!rows.length) return null;
          return <div className="review-group" key={section.id} data-testid={`admin-drawer-section-${section.id}`}>
            <div className="review-group-head"><h3>{section.title}</h3></div>
            <dl className="review-rows">{rows.map(f => <div className="review-row" key={f.key} data-testid={`admin-drawer-row-${f.key}`}>
              <dt>{f.label}</dt><dd>{answerText(entry.answers[f.key])}</dd>
            </div>)}</dl>
          </div>;
        })}
        <p style={{ marginTop: 22, fontSize: 12, color: '#8d99b0' }} data-testid="admin-drawer-updated">Last updated {formatDateTime(entry.updated_at)}</p>
      </div>
    </SheetContent>
  </Sheet>;
};
